/**
 * Lightweight template metadata for landing page and previews
 * This file contains only the metadata without the heavy flow_data
 * to reduce bundle size on pages that don't need the full template data.
 */

export interface TemplateMetadata {
  id: string;
  name: string;
  description: string;
  category: string;
  backgroundImage?: string;
  thumbnail?: string;
  nodeCount: number;
}

export const templateMetadata: TemplateMetadata[] = [
  {
    id: 'generate-images-scenarios',
    name: 'Generate Images – Starter Scenarios',
    description:
      'Three ways to generate images: simple prompt, AI-written prompt, and reference image editing, each with inline documentation and a video walkthrough.',
    category: 'Image',
    backgroundImage:
      'https://media.easy-peasy.ai/ff5e9a62-f4f6-4c39-b2c1-8294b2426304/f4d15a6e-dcf9-4746-b497-441471e40581.png',
    nodeCount: 15,
  },
  {
    id: 'generate-video-scenarios',
    name: 'Generate Videos – Starter Scenarios',
    description:
      'Four powerful ways to create videos: from text, from images, using frame interpolation, and by editing images before animation.',
    category: 'Video',
    backgroundImage:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/9d34dbec-d616-493c-b685-4b9025ac2d94.png',
    nodeCount: 19,
  },
  {
    id: 'singapore-knitted-city',
    name: 'Singapore Knitted City - Short Video',
    description:
      'Transform multiple Singapore landmark images into knitted fabric style and create animated videos, culminating in a special animation of the word "SINGAPORE" being knitted letter by letter.',
    category: 'Video',
    backgroundImage:
      'https://media.easy-peasy.ai/assets/ca4c3cad-78ea-42b6-90b9-8d4950a6a499.mp4',
    nodeCount: 76,
  },
  {
    id: 'virtual-try-on',
    name: 'Virtual Try-On',
    description:
      'Generate a model image, dress them with uploaded clothing items using AI image editing, and animate the result into a video with AI-generated motion.',
    category: 'Image',
    backgroundImage: 'https://media.easy-peasy.ai/assets/try-on.jpeg',
    nodeCount: 7,
  },
  {
    id: 'virtual-try-on-hijab-abaya',
    name: 'Virtual Try-On: Hijab & Abaya',
    description:
      'Generate a Malay woman model, dress them with hijab and abaya clothing items using AI image editing, and animate the result into a promotional video.',
    category: 'Image',
    backgroundImage:
      'https://media.easy-peasy.ai/8a8096ae-bdbf-480e-a053-760d699da41c/ddbb208f-e00b-4b6e-8776-15c94de02160.png',
    nodeCount: 7,
  },
  {
    id: 'interior-design-visualization',
    name: 'Interior Design Visualization',
    description:
      'Transform a 2D floor plan into a stunning 3D isometric maquette, then generate photorealistic room renders and create an animated video tour of the entire property.',
    category: 'Video',
    backgroundImage:
      'https://media.easy-peasy.ai/assets/0d6ab58a-9793-4526-9186-edc1cf0bb2b6.mp4',
    nodeCount: 16,
  },
  {
    id: 'fashion-product-video-multi-scene',
    name: 'Fashion Product Video - Multi-Scene Showcase',
    description:
      'Create a stunning fashion product showcase video by combining a model reference with a product image, generating multiple scene variations, animating each scene, and merging them into a polished multi-scene commercial.',
    category: 'Video',
    backgroundImage:
      'https://media.easy-peasy.ai/assets/809bea1d-cd33-4de8-bb19-cd1c406ada4f.mp4',
    nodeCount: 14,
  },
  {
    id: 'contact-sheet-fashion-video',
    name: 'Contact Sheet Fashion Video Production',
    description:
      'Advanced fashion video workflow using Contact Sheet Prompting with Nano Banana Pro. Upload a model image, apply wardrobe styling, generate 6 dynamic camera angle keyframes, then create multiple cinematic video clips with Veo 3.1 first-last frame animation.',
    category: 'Video',
    backgroundImage: 'https://media.easy-peasy.ai/assets/contact_sheet.jpg',
    nodeCount: 15,
  },
];

/**
 * Get template metadata by ID
 */
export function getTemplateMetadataById(
  id: string,
): TemplateMetadata | undefined {
  return templateMetadata.find((template) => template.id === id);
}

/**
 * Get template metadata by category
 */
export function getTemplateMetadataByCategory(
  category: string,
): TemplateMetadata[] {
  return templateMetadata.filter((template) => template.category === category);
}

/**
 * Get all template categories
 */
export function getTemplateCategories(): string[] {
  const categories = new Set(
    templateMetadata.map((template) => template.category),
  );
  return Array.from(categories);
}
